import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Phone,
  Upload,
  CheckCircle2,
  Ruler,
  Sparkles,
  Building,
  Hammer,
  FileText,
  ArrowRight,
} from 'lucide-react';
import LandingNav, { PHONE_DISPLAY, PHONE_TEL } from '@/components/LandingNav';
import LandingFooter from '@/components/LandingFooter';
import SchemaMarkup from '@/components/SchemaMarkup';
import { buildWebPageSchema } from '@/lib/schema';
import OpeningCatalog from '@/components/OpeningCatalog';
import HowItWorks5 from '@/components/HowItWorks5';
import PricingFrameBlock from '@/components/PricingFrameBlock';
import SEO from '@/components/SEO';

const ECO_LOGO =
  'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/w9jekft2_eco_window_systems_logo%20%281%29.jpg';
const MR_GLASS_LOGO =
  'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/1edhksrg_Mr-Glass-Logo.png';
const ES_LOGO =
  'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/dt288m9t_ESWindow-Logo_Web.png';

const trackPixel = (event, data = {}) => {
  if (window.fbq) {
    if (['Lead', 'InitiateCheckout', 'CompleteRegistration'].includes(event)) {
      window.fbq('track', event, data);
    } else {
      window.fbq('trackCustom', event, data);
    }
  }
};
const trackGA = (event, data = {}) => {
  if (window.gtag) window.gtag('event', event, data);
};

export default function LandingCostMiami() {
  useEffect(() => {
    trackPixel('ViewContent', { content_name: 'Landing Cost Miami' });
    trackGA('view_landing_cost_miami', {
      event_category: 'paid_traffic',
      event_label: 'cost_miami',
    });
  }, []);

  const trackCta = (label) =>
    trackGA('cta_click_cost_miami', {
      event_category: 'engagement',
      event_label: label,
    });

  const webPageSchema = buildWebPageSchema({
    url: '/impact-window-cost-miami',
    name: 'Impact Window Cost in Miami — Real Pricing for South Florida Homes',
    description:
      'Transparent impact window and door pricing for Miami homes — Good/Better/Premium tiers. Get a quote without an in-home appointment.',
  });

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <SEO
        title="Impact Window Cost in Miami — Real Pricing for South Florida Homes"
        description="Transparent impact window and door pricing for Miami homes — Good/Better/Premium tiers. Get a quote without an in-home appointment."
        path="/impact-window-cost-miami"
      />
      <SchemaMarkup schemas={webPageSchema} instanceId="lp-cost-miami" />
      <LandingNav logoTo="/impact-window-cost-miami" />

      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--brand-ocean-100)]/40 via-white to-white pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-12 pb-10 md:pt-16 md:pb-14 text-center">
          <Badge
            variant="outline"
            className="rounded-full px-3 py-1 text-[11px] uppercase tracking-wider border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] bg-white"
          >
            Miami • Transparent Pricing
          </Badge>
          <h1
            className="mt-4 font-heading text-4xl sm:text-5xl md:text-6xl font-semibold leading-[1.05] tracking-tight text-[var(--brand-slate-900)]"
            data-testid="hero-headline"
          >
            How Much Do Impact Windows Cost in{' '}
            <span className="text-[var(--brand-ocean-700)]">Miami?</span>
          </h1>
          <p
            className="mt-5 text-lg sm:text-xl text-[var(--brand-slate-600)] max-w-2xl mx-auto"
            data-testid="hero-subheadline"
          >
            Most full-home projects range from{' '}
            <span className="font-semibold text-[var(--brand-slate-900)]">$15,000 to $40,000+</span>.
            Get exact pricing for your home in ~24 hours — no in-home appointment required.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              asChild
              size="lg"
              className="h-14 px-7 rounded-xl text-base font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-md hover:shadow-lg"
              data-testid="hero-cta-get-quote"
            >
              <Link to="/get-quote" onClick={() => trackCta('hero')}>
                <Upload className="mr-2 h-5 w-5" />
                Get My Price
              </Link>
            </Button>
            <a
              href={PHONE_TEL}
              className="inline-flex items-center justify-center h-14 px-6 rounded-xl text-base font-semibold border-2 border-[hsl(var(--border))] text-[var(--brand-slate-800)] hover:bg-[hsl(var(--secondary))]"
              onClick={() =>
                trackGA('phone_call_click', {
                  event_category: 'engagement',
                  event_label: 'hero_call_cost_miami',
                })
              }
              data-testid="hero-call-cta"
            >
              <Phone className="mr-2 h-5 w-5" />
              Or call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      {/* ===== Quick answer (the headline range) ===== */}
      <section
        className="py-10 sm:py-14 bg-[hsl(var(--secondary))]/40 border-y border-[hsl(var(--border))]"
        data-testid="quick-answer-section"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
            Quick answer
          </p>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-semibold text-[var(--brand-slate-900)]">
            What should a Miami homeowner expect to pay?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[var(--brand-slate-700)] leading-relaxed">
            Most full-home impact window and door projects in Miami fall between{' '}
            <span className="font-semibold text-[var(--brand-ocean-700)]">$15,000 and $40,000+</span>.
            Smaller partial replacements can come in lower, but the typical range above is what
            most homeowners should plan for.
          </p>
          <p className="mt-3 text-sm sm:text-base text-[var(--brand-slate-600)] leading-relaxed">
            Final pricing depends on number of openings, product tier, glass spec, and
            installation complexity. Permit processing is billed as a separate{' '}
            <span className="font-semibold text-[var(--brand-slate-800)]">$500 line item</span>;
            actual city / Miami-Dade permit fees are excluded and can vary.
          </p>
        </div>
      </section>

      {/* ===== Pricing Framing Block (refresh) ===== */}
      <PricingFrameBlock src="cost_miami_pricing_frame" />

      {/* ===== How It Works (refresh, compact) ===== */}
      <HowItWorks5 src="cost_miami_how_it_works" variant="compact" />

      {/* ===== Real pricing examples ===== */}
      <section className="py-14 sm:py-20" data-testid="pricing-examples-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
              Real pricing examples
            </p>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)]">
              Per-unit pricing across Good, Better, and Premium
            </h2>
            <p className="mt-3 text-[var(--brand-slate-600)]">
              Real installed unit pricing on common Miami sizes. These are starting points — your
              quote will reflect your exact home, glass spec, and install conditions.
            </p>
          </div>

          {/* Pricing examples grid */}
          <div className="mt-8 sm:mt-10 space-y-4 sm:space-y-5">
            {[
              {
                id: 'window-37x51',
                size: '37" × 51" Impact Window',
                desc: 'A common bedroom / living-room single-hung size in Miami homes.',
                eco: '$930',
                mrglass: '$960',
                es: '$1,003',
              },
              {
                id: 'slider-72x80',
                size: '72" × 80" Sliding Glass Door',
                desc: 'A typical patio / lanai 6-foot impact slider.',
                eco: '$2,500',
                mrglass: '$2,800',
                es: '$3,100',
              },
            ].map((row) => (
              <Card
                key={row.id}
                className="rounded-2xl border border-[hsl(var(--border))] bg-white"
                data-testid={`pricing-example-${row.id}`}
              >
                <CardContent className="p-5 sm:p-6">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <h3 className="font-heading text-lg sm:text-xl font-semibold text-[var(--brand-slate-900)]">
                      {row.size}
                    </h3>
                  </div>
                  <p className="mt-1 text-sm text-[var(--brand-slate-600)]">{row.desc}</p>

                  <div className="mt-5 grid grid-cols-3 gap-3 sm:gap-4">
                    {[
                      { tier: 'Good', brand: 'ECO', price: row.eco },
                      { tier: 'Better', brand: 'Mr. Glass', price: row.mrglass, featured: true },
                      { tier: 'Premium', brand: 'ES Elite', price: row.es },
                    ].map((c) => (
                      <div
                        key={c.tier}
                        className={`rounded-xl px-3 py-3 sm:px-4 sm:py-4 text-center ${
                          c.featured
                            ? 'bg-[var(--brand-ocean-100)]/40 border-2 border-[var(--brand-ocean-600)]'
                            : 'bg-[hsl(var(--secondary))]/50 border border-[hsl(var(--border))]'
                        }`}
                      >
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
                          {c.tier}
                        </p>
                        <p className="mt-0.5 text-xs font-medium text-[var(--brand-slate-600)]">
                          {c.brand}
                        </p>
                        <p className="mt-1 font-heading text-lg sm:text-xl font-semibold text-[var(--brand-slate-900)]">
                          {c.price}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="mt-5 text-xs sm:text-sm text-[var(--brand-slate-500)] leading-relaxed">
            Prices reflect the unit only. Installation, removal, finish carpentry, and permits are
            quoted separately and packaged into your final number.
          </p>
        </div>
      </section>

      {/* ===== Good / Better / Premium with logos ===== */}
      <section
        className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/40"
        data-testid="tiers-section"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
              Your options
            </p>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)]">
              Three tiers — you choose what fits your home
            </h2>
          </div>

          <div className="mt-8 sm:mt-10 grid md:grid-cols-3 gap-4 sm:gap-5">
            {[
              { id: 'good', tier: 'Good', brand: 'ECO Windows', desc: 'Affordable, code-compliant', logo: ECO_LOGO },
              { id: 'better', tier: 'Better', brand: 'Mr. Glass', desc: 'Stronger frames, better performance', logo: MR_GLASS_LOGO, featured: true },
              { id: 'premium', tier: 'Premium', brand: 'ES Windows', desc: 'Top-tier design and durability', logo: ES_LOGO },
            ].map((t) => (
              <Card
                key={t.id}
                className={`rounded-2xl ${
                  t.featured
                    ? 'border-2 border-[var(--brand-ocean-600)] bg-white shadow-sm'
                    : 'border border-[hsl(var(--border))] bg-white'
                }`}
                data-testid={`tier-card-${t.id}`}
              >
                <CardContent className="p-5 sm:p-6 text-center flex flex-col items-center">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
                    {t.tier}
                  </p>
                  <div className="mt-3 h-14 sm:h-16 flex items-center justify-center w-full">
                    <img
                      src={t.logo}
                      alt={`${t.brand} logo`}
                      loading="lazy"
                      draggable={false}
                      className="max-h-full max-w-[160px] object-contain select-none pointer-events-none"
                      data-testid={`tier-logo-${t.id}`}
                    />
                  </div>
                  <h3 className="mt-3 font-heading text-lg sm:text-xl font-semibold text-[var(--brand-slate-900)]">
                    {t.brand}
                  </h3>
                  <p className="mt-1.5 text-sm text-[var(--brand-slate-600)] leading-relaxed">
                    {t.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="mt-7 text-center text-sm sm:text-base text-[var(--brand-slate-600)] max-w-2xl mx-auto">
            We show you all three options so you can choose what works best for your home and budget.
          </p>
        </div>
      </section>

      {/* ===== Opening Catalog (refresh, compact) ===== */}
      <OpeningCatalog src="cost_miami_catalog" variant="compact" />

      {/* ===== What affects cost (tightened to 4 factors) ===== */}
      <section className="py-12 sm:py-16" data-testid="cost-factors-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
              What changes your price
            </p>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)]">
              Four factors that drive your final number
            </h2>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-4 sm:gap-5">
            {[
              {
                icon: Ruler,
                title: 'Number of openings',
                desc: 'A few windows vs. a full home (8–20+ openings) is the biggest single driver.',
              },
              {
                icon: Sparkles,
                title: 'Product tier & glass',
                desc: 'ECO, Mr. Glass, or ES Windows — plus laminated, low-E, or tinted glass options.',
              },
              {
                icon: Building,
                title: 'Property type',
                desc: 'Condos and high-rises (HOA approval, lift access) cost more than single-family.',
              },
              {
                icon: Hammer,
                title: 'Install complexity',
                desc: 'Stucco vs. CBS, openings that need reframing, and finish carpentry needs.',
              },
            ].map((f) => (
              <Card
                key={f.title}
                className="rounded-2xl border border-[hsl(var(--border))] bg-white"
                data-testid={`cost-factor-${f.title.toLowerCase().replace(/\W+/g, '-')}`}
              >
                <CardContent className="p-5 sm:p-6 flex gap-4">
                  <div className="h-11 w-11 rounded-xl bg-[var(--brand-ocean-100)] flex items-center justify-center shrink-0">
                    <f.icon className="h-5 w-5 text-[var(--brand-ocean-700)]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-[var(--brand-slate-900)]">
                      {f.title}
                    </h3>
                    <p className="mt-1 text-sm text-[var(--brand-slate-600)] leading-relaxed">
                      {f.desc}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Real proof ===== */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/40" data-testid="proof-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-ocean-700)]">
            Real proof
          </p>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-semibold text-[var(--brand-slate-900)]">
            Real Projects, Real Pricing
          </h2>
          <div className="mt-7 sm:mt-9 divide-y divide-[hsl(var(--border))]">
            {[
              {
                id: 'kendall',
                title: 'Kendall Full Home',
                stat: '~$28,400',
                desc: 'Complete impact window and door replacement using ES Windows',
              },
              {
                id: 'north-miami',
                title: 'North Miami Condo',
                stat: '~$25,000',
                desc: 'HOA-compliant installation with coastal-grade materials (Mr. Glass)',
              },
            ].map((cs) => (
              <div
                key={cs.id}
                className="py-5 sm:py-6 first:pt-0 last:pb-0"
                data-testid={`proof-item-${cs.id}`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <h3 className="font-heading text-lg sm:text-xl font-semibold text-[var(--brand-slate-900)]">
                    {cs.title}
                  </h3>
                  <span className="font-heading text-lg sm:text-xl font-semibold text-[var(--brand-ocean-700)] whitespace-nowrap">
                    — {cs.stat}
                  </span>
                </div>
                <p className="mt-1.5 text-sm sm:text-[15px] text-[var(--brand-slate-600)] leading-relaxed">
                  {cs.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Permits & fees disclosure ===== */}
      <section className="py-10 sm:py-12" data-testid="permits-disclosure-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Card className="rounded-2xl border-2 border-[var(--brand-ocean-600)] bg-[var(--brand-ocean-100)]/15">
            <CardContent className="p-5 sm:p-6 flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-[var(--brand-ocean-700)] flex items-center justify-center shrink-0">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-[var(--brand-slate-900)]">
                  Transparent on permits & fees
                </h3>
                <ul className="mt-3 space-y-2 text-sm sm:text-[15px] text-[var(--brand-slate-700)]">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] mt-0.5 shrink-0" />
                    <span>
                      Permit processing is a separate{' '}
                      <span className="font-semibold">$500 line item</span> on every quote.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] mt-0.5 shrink-0" />
                    <span>
                      City and Miami-Dade County permit fees are{' '}
                      <span className="font-semibold">excluded and can vary</span> by jurisdiction.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] mt-0.5 shrink-0" />
                    <span>You always see the full breakdown before you decide — no surprises.</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ===== Final CTA → /get-quote ===== */}
      <section
        className="py-14 sm:py-20 bg-[hsl(var(--secondary))]/40"
        data-testid="final-cta-section"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="rounded-3xl border-2 border-[var(--brand-ocean-600)] bg-white p-8 sm:p-12 text-center shadow-sm">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[var(--brand-slate-900)]">
              See exact pricing for your home
            </h2>
            <p className="mt-3 text-base sm:text-lg text-[var(--brand-slate-700)] max-w-xl mx-auto leading-relaxed">
              Upload a few photos and we’ll send Good, Better, and Premium pricing tailored to your
              project — usually within 24 hours.
            </p>
            <div className="mt-8">
              <Button
                asChild
                size="lg"
                className="h-14 px-8 rounded-xl text-base font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-md"
                data-testid="final-cta-button"
              >
                <Link to="/get-quote" onClick={() => trackCta('final_cta')}>
                  <Upload className="mr-2 h-5 w-5" />
                  Get My Price
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
            <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs sm:text-sm text-[var(--brand-slate-600)]">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)]" />
                No appointment
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)]" />
                No pressure
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)]" />
                Quote in ~24 hours
              </span>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
